// Analytics configuration and utilities

export const ANALYTICS_CONFIG = {
  // Umami Analytics configuration
  websiteId: process.env.NEXT_PUBLIC_UMAMI_WEBSITE_ID || '514938a7-9607-4c4e-98e8-7731a483ee97',
  umamiUrl: process.env.NEXT_PUBLIC_UMAMI_URL || 'https://cloud.umami.is',
  
  // Custom events for tracking
  events: {
    // User interactions
    SIGN_UP: 'sign_up',
    SIGN_IN: 'sign_in',
    PET_SEARCH: 'pet_search',
    PET_FOUND: 'pet_found',
    PET_NOT_FOUND: 'pet_not_found',
    
    // E-commerce
    ADD_TO_CART: 'add_to_cart',
    PURCHASE: 'purchase',
    MEMORABILIA_ORDER: 'memorabilia_order',
    
    // Training
    COURSE_START: 'course_start',
    COURSE_COMPLETE: 'course_complete',
    CERTIFICATE_DOWNLOAD: 'certificate_download',
    
    // Support
    CONTACT_SUPPORT: 'contact_support',
    SUPPORT_REQUEST: 'support_request',
    
    // Clinic interactions
    CLINIC_REGISTRATION: 'clinic_registration',
    CLINIC_LOGIN: 'clinic_login',
    
    // Page interactions
    BUTTON_CLICK: 'button_click',
    FORM_SUBMIT: 'form_submit',
    LINK_CLICK: 'link_click',
  },
  
  // Page categories for better organization
  pageCategories: {
    HOME: 'home',
    ABOUT: 'about',
    SERVICES: 'services',
    TRAINING: 'training',
    SUPPORT: 'support',
    DASHBOARD: 'dashboard',
    PET_SEARCH: 'pet_search',
    CART: 'cart',
    CHECKOUT: 'checkout',
  }
}

// Utility function to track custom events
export function trackEvent(eventName: string, props?: Record<string, any>) {
  if (typeof window !== 'undefined' && window.umami) {
    window.umami.track(eventName, props)
  }
}

// ── Google Ads (gtag) conversion tracking ──────────────────────────
// The base tag (AW-18227183677) is loaded site-wide in layout.tsx for the
// Loved Pets ad campaign. To record a *conversion* you also need a conversion
// action's "send_to" label (e.g. 'AW-18227183677/AbC-dEfGh'), created in
// Google Ads → Goals → Conversions. Set it via NEXT_PUBLIC_GOOGLE_ADS_LOVEDPETS_CONVERSION
// (or hardcode it below). Until it's set, the conversion fire is a safe no-op.
export const GOOGLE_ADS_ID = 'AW-18227183677'

const LOVEDPETS_CONVERSION_SEND_TO =
  process.env.NEXT_PUBLIC_GOOGLE_ADS_LOVEDPETS_CONVERSION || ''

/**
 * Fire a Google Ads conversion. No-op when gtag hasn't loaded or no `sendTo`
 * label is configured — so it's safe to call before the conversion action
 * exists / before the ad is approved.
 */
export function trackGoogleAdsConversion(sendTo: string, props?: Record<string, any>) {
  if (typeof window === 'undefined') return
  const gtag = (window as any).gtag
  if (typeof gtag !== 'function' || !sendTo) return
  gtag('event', 'conversion', { send_to: sendTo, ...props })
}

/**
 * Like trackGoogleAdsConversion, but resolves once gtag has sent the hit (or
 * after `timeoutMs`), so a caller that navigates away straight after a form
 * submit doesn't cancel the request. Resolves immediately when it's a no-op.
 */
export function trackGoogleAdsConversionBeforeNavigate(
  sendTo: string,
  props?: Record<string, any>,
  timeoutMs = 1000
): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve()
  const gtag = (window as any).gtag
  if (typeof gtag !== 'function' || !sendTo) return Promise.resolve()
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, timeoutMs)
    gtag('event', 'conversion', {
      send_to: sendTo,
      ...props,
      event_callback: () => {
        clearTimeout(timer)
        resolve()
      },
    })
  })
}

/** Loved Pets CTA conversion — fires once the conversion label is configured. */
export function trackLovedPetsConversion(props?: Record<string, any>) {
  trackGoogleAdsConversion(LOVEDPETS_CONVERSION_SEND_TO, props)
}

// ── Meta (Facebook/Instagram) Pixel ────────────────────────────────
// The base pixel is loaded site-wide in layout.tsx ONLY when
// NEXT_PUBLIC_META_PIXEL_ID is set, so this is a safe no-op until the
// disruption ad campaign goes live and the id is configured.
/**
 * Fire a Meta Pixel standard/custom event. No-op when `fbq` hasn't loaded
 * (pixel id unset, ad blocker, SSR).
 */
export function trackMetaEvent(event: string, props?: Record<string, any>) {
  if (typeof window === 'undefined') return
  const fbq = (window as any).fbq
  if (typeof fbq !== 'function') return
  fbq('track', event, props)
}

// ── OpenAI (ChatGPT ads) Measurement Pixel ─────────────────────────
// Loaded site-wide in layout.tsx ONLY when NEXT_PUBLIC_OPENAI_PIXEL_ID is set,
// so this is a safe no-op until then. Amounts are integer cents. `event_id`
// is a stable per-order id so a later server-side Conversions API send (or a
// repeat browser fire) is de-duplicated rather than double-counted.
// https://developers.openai.com/ads/measurement-pixel
/** Fire an OpenAI pixel event. No-op when `oaiq` hasn't loaded. */
export function trackOpenAIEvent(
  event: string,
  data: Record<string, any>,
  options?: { event_id?: string }
) {
  if (typeof window === 'undefined') return
  const oaiq = (window as any).oaiq
  if (typeof oaiq !== 'function') return
  oaiq('measure', event, data, options)
}

// Per-campaign Google Ads conversion labels (created in Google Ads → Goals →
// Conversions). Until set, the gtag fire is a safe no-op.
const VETS_CONVERSION_SEND_TO =
  process.env.NEXT_PUBLIC_GOOGLE_ADS_VETS_CONVERSION || ''
const OWNERS_CONVERSION_SEND_TO =
  process.env.NEXT_PUBLIC_GOOGLE_ADS_OWNERS_CONVERSION || ''
// Direct offer: the intake form (lead) and the confirmed order (purchase) are
// separate conversion actions, so each needs its own label — sharing one would
// count every completed order twice.
const DIRECT_CONVERSION_SEND_TO =
  process.env.NEXT_PUBLIC_GOOGLE_ADS_DIRECT_CONVERSION || ''
const DIRECT_PURCHASE_CONVERSION_SEND_TO =
  process.env.NEXT_PUBLIC_GOOGLE_ADS_DIRECT_PURCHASE_CONVERSION || ''

/**
 * Problem-awareness campaign — vet clinic lead (e.g. waitlist enquiry from
 * /for-vets). Records the lead across all three channels at once; each
 * underlying call is independently no-op-safe.
 */
export function trackVetCampaignConversion(props?: Record<string, any>) {
  trackEvent('vets_campaign_lead', props)
  trackGoogleAdsConversion(VETS_CONVERSION_SEND_TO, props)
  trackMetaEvent('Lead', { content_name: 'for-vets', ...props })
  trackOpenAIEvent('lead_created', { type: 'customer_action' })
}

/**
 * Problem-awareness campaign — pet owner engagement signal (e.g. sharing
 * /choose). Awareness play, no data capture. No-op-safe across Umami /
 * Google Ads / Meta.
 */
export function trackOwnerCampaignConversion(props?: Record<string, any>) {
  trackEvent('owners_campaign_lead', props)
  trackGoogleAdsConversion(OWNERS_CONVERSION_SEND_TO, props)
  trackMetaEvent('Lead', { content_name: 'choose', ...props })
}

/**
 * Direct-to-owner offer — an owner submitted the home-page intake form (a
 * draft order exists; they're on their way to the checkout). This is the lead
 * event the Google / Meta ads optimise on. No-op-safe. Await it before
 * navigating so the Google Ads hit isn't cancelled by the page unload.
 */
export function trackDirectConversion(props?: Record<string, any>): Promise<void> {
  trackEvent('direct_start', props)
  trackMetaEvent('Lead', { content_name: 'direct', ...props })
  trackOpenAIEvent(
    'lead_created',
    { type: 'customer_action' },
    props?.reference ? { event_id: `${props.reference}-lead` } : undefined
  )
  return trackGoogleAdsConversionBeforeNavigate(
    DIRECT_CONVERSION_SEND_TO,
    props?.reference ? { transaction_id: String(props.reference) } : undefined
  )
}

/**
 * Direct-to-owner offer — the owner confirmed their selections (bank transfer
 * recorded or card payment made). `value` is the NZD total. No-op-safe.
 */
export function trackDirectPurchase(props?: Record<string, any>) {
  trackEvent(ANALYTICS_CONFIG.events.PURCHASE, { source: 'direct', ...props })
  trackGoogleAdsConversion(DIRECT_PURCHASE_CONVERSION_SEND_TO, {
    value: Number(props?.value ?? 0),
    currency: 'NZD',
    ...(props?.reference ? { transaction_id: String(props.reference) } : {}),
  })
  trackMetaEvent('Purchase', { currency: 'NZD', content_name: 'direct', ...props })
  trackOpenAIEvent(
    'order_created',
    {
      type: 'contents',
      amount: Math.round(Number(props?.value ?? 0) * 100),
      currency: 'NZD',
      contents: [{ id: 'direct', name: 'PetAftercare Direct', content_type: 'product', quantity: 1 }],
    },
    props?.reference ? { event_id: String(props.reference) } : undefined
  )
}

// Utility function to track page views with categories
export function trackPageView(pageName: string, category?: string) {
  if (typeof window !== 'undefined' && window.umami) {
    const eventData = category ? { category } : undefined
    window.umami.track('pageview', eventData)
  }
}

// Utility function to track user interactions
export function trackUserInteraction(action: string, element: string, props?: Record<string, any>) {
  trackEvent('user_interaction', {
    action,
    element,
    ...props
  })
}

export type ErrorCategory =
  | 'rate_limited'
  | 'validation'
  | 'server'
  | 'network'
  | 'unknown'

// Map an HTTP status and/or thrown error to a low-cardinality category.
// Use at fetch failure sites so dashboards group errors instead of fanning
// out on free-form messages.
export function classifyError(opts: { status?: number; err?: unknown }): {
  error_code: ErrorCategory
  http_status?: number
} {
  const { status, err } = opts
  if (status === 429) return { error_code: 'rate_limited', http_status: status }
  if (status && status >= 500) return { error_code: 'server', http_status: status }
  if (status && status >= 400) return { error_code: 'validation', http_status: status }
  // No HTTP status — fetch threw (network blip, CORS, abort)
  if (err instanceof TypeError) return { error_code: 'network' }
  return { error_code: 'unknown' }
}
